import * as React from 'react'
import { NavLink, Link } from 'react-router-dom'
import { LayoutDashboard, ImagePlus, Image, ListOrdered, Clapperboard, Package, HelpCircle, X, ChevronRight, BookOpen } from 'lucide-react'

import { LogoWithText } from '@/components/ui/logo'
import { cn } from '@/lib/utils'

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/create', label: 'Create Ad', icon: Clapperboard, primary: true },
  { to: '/create-image', label: 'Create Image', icon: ImagePlus },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/saved-context', label: 'Saved Context', icon: Image },
  { to: '/created-assets', label: 'Created Assets', icon: ListOrdered },
]

const DOCS_NAV = { to: '/docs', label: 'Docs', icon: BookOpen }

const SEEDANCE_HELP: { section: string; items: { title: string; subtitle?: string; body: string }[] }[] = [
  {
    section: 'Models',
    items: [
      {
        title: 'Text → Video',
        subtitle: '$0.07/sec · 1080p · up to 15s',
        body: 'No image input. Use when you have a strong written scene description. Best for lifestyle B-roll and concept tests.',
      },
      {
        title: 'Multimodal (default)',
        subtitle: '$0.08/sec · 1080p · up to 15s',
        body: 'Accepts up to 9 reference images + 3 video refs. The model uses them to lock down product appearance across scenes. Use this for real product shots.',
      },
      {
        title: 'Fast Multimodal',
        subtitle: '$0.04/sec · 720p · max 10s',
        body: 'Half price, lower quality. Use for quick prompt testing before committing to a full render.',
      },
    ],
  },
  {
    section: 'Prompt formula',
    items: [
      {
        title: 'Structure',
        body: '[product placement] + [environment] + [camera move] + [lighting] + [motion detail] + quality suffix',
      },
      {
        title: 'Camera moves',
        body: 'slow forward dolly · dolly out · smooth lateral pan · tracking shot · camera holds steady · low angle upward',
      },
      {
        title: 'Lighting',
        body: 'golden hour backlight · soft diffused studio key + rim · single spotlight on black · early morning window light · moody candlelight',
      },
      {
        title: 'Quality suffix',
        body: 'End every prompt with: cinematic, smooth motion, premium product advertisement aesthetic',
      },
      {
        title: 'Negative prompt',
        body: 'blur, distort, low quality, pixelated, grainy, watermark, text overlay, logo, amateur, shaky, jitter',
      },
      {
        title: 'Image references',
        body: 'Attach images in the prompt input. The AI binds them to your scenes automatically — front angles, lifestyle shots, character refs all work. You can describe them in the prompt the way a director would ("the woman picks up the bottle from the marble vanity").',
      },
      {
        title: 'Multiple images',
        body: 'Up to 9 images per generation. The more consistent angles you provide, the more accurately the model holds product appearance across frames.',
      },
    ],
  },
  {
    section: 'Todos',
    items: [
      {
        title: 'Prompt templates — image-aware',
        body: 'Update templates to incorporate the primary image into the scene description by default when one is attached. Currently templates are text-only.',
      },
      {
        title: 'Video analysis & tagging',
        body: 'Auto-tag uploaded reference videos with scene_type, camera_movement, lighting, and pacing after Gemini analysis. Currently scene_type is set at upload but not from analysis output.',
      },
      {
        title: 'Multi-image reference UI',
        body: 'The prompt input only shows a URL list. Build a thumbnail strip so users can see each attached image and its order before generating.',
      },
      {
        title: 'Create Ad Step 1 — knowledge base & templates',
        body: 'Wire product knowledge base (benefits, brand voice, price) and prompt templates into Step 1 of Create Ad. User should be able to pick a template and/or select a product to pre-fill the idea field before generating the script.',
      },
    ],
  },
]

function HelpPanel({ onClose }: { onClose: () => void }) {
  const [expanded, setExpanded] = React.useState<string | null>('Models')

  return (
    <div className="absolute bottom-14 left-0 w-72 z-50 rounded-xl border border-border bg-popover shadow-2xl">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <span className="text-sm font-semibold text-foreground">Seedance Quick Reference</span>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="max-h-[480px] overflow-y-auto divide-y divide-border">
        {SEEDANCE_HELP.map(({ section, items }) => (
          <div key={section}>
            <button
              onClick={() => setExpanded(expanded === section ? null : section)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
            >
              {section}
              <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expanded === section && 'rotate-90')} />
            </button>
            {expanded === section && (
              <div className="px-4 pb-3 space-y-3">
                {items.map((item) => (
                  <div key={item.title} className="space-y-0.5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-foreground">{item.title}</span>
                      {item.subtitle && (
                        <span className="text-[10px] text-muted-foreground">{item.subtitle}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="px-4 py-2.5 border-t border-border">
        <Link
          to="/docs"
          className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          Open full docs &rarr;
        </Link>
      </div>
    </div>
  )
}

export function Sidebar() {
  const [helpOpen, setHelpOpen] = React.useState(false)

  return (
    <aside className="hidden md:flex md:w-60 shrink-0 flex-col border-r border-border bg-popover h-screen sticky top-0">
      <div className="h-16 flex items-center px-6 border-b border-border">
        <LogoWithText size="sm" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col">
        <div className="space-y-1">
          {NAV.map(({ to, label, icon: Icon, primary }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-l-2 border-foreground bg-accent/50 text-foreground'
                    : primary
                    ? 'text-foreground hover:bg-accent'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <Icon className={cn('h-4 w-4', primary && 'text-foreground')} />
              {label}
            </NavLink>
          ))}
        </div>
        <div className="mt-auto pt-4">
          <NavLink
            to={DOCS_NAV.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-l-2 border-foreground bg-accent/50 text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <DOCS_NAV.icon className="h-4 w-4" />
            {DOCS_NAV.label}
          </NavLink>
        </div>
      </nav>
      <div className="relative px-3 py-4 border-t border-border flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground px-3">v1.0</span>
        <button
          onClick={() => setHelpOpen((v) => !v)}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
            helpOpen
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          )}
        >
          <HelpCircle className="h-3.5 w-3.5" />
          Help
        </button>
        {helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}
      </div>
    </aside>
  )
}

export function BottomNav() {
  const bottomNavItems = NAV
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-popover">
      <ul className="grid grid-cols-6">
        {bottomNavItems.map(({ to, label, icon: Icon }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center gap-1 py-2 text-[11px]',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="h-4 w-4" />
                  {label}
                  <span
                    className={cn(
                      'h-1 w-1 rounded-full transition-opacity',
                      isActive ? 'bg-foreground opacity-100' : 'opacity-0',
                    )}
                    aria-hidden="true"
                  />
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
